import { useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import { api, t } from "../lib/api";
import { fmtDate, fmtDuration, useAsync } from "../lib/hooks";
import { useSession } from "../lib/session";
import { ErrorNote } from "../components/ui";
import { roleLabel } from "../components/Shell";

const ROLES = ["admin", "lead", "reviewer", "interviewer", "client_viewer"];

export function TeamPage() {
  const { tenant } = useParams();
  const { me } = useSession();
  const members = useAsync(() => api.get<Array<{ id: string; email: string; name: string | null; role: string; last_login_at: string | null }>>(`/api/t/${tenant}/members`), [tenant]);
  const usage = useAsync(() => api.get<{ plan: string; remainingSeconds: number | null; months: Array<{ period: string; interview_seconds: number; ai_input_tokens: number; ai_output_tokens: number }> }>(`/api/t/${tenant}/usage`), [tenant]);
  const audit = useAsync(() => api.get<Array<{ id: number; at: string; action: string; actor_email: string | null; target_type: string | null }>>(`/api/t/${tenant}/audit`), [tenant]);
  const [f, setF] = useState({ email: "", role: "interviewer" });
  const [error, setError] = useState<Error | null>(null);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await api.post(`/api/t/${tenant}/members`, f);
      setF({ ...f, email: "" });
      await members.reload();
    } catch (err) {
      setError(err as Error);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Team and access</h1>
          <p className="sub">Who can use this client workspace, and what they can do.</p>
        </div>
      </div>
      <div className="stack" style={{ gap: 20 }}>
        <section className="panel flush">
          <table className="table">
            <thead>
              <tr>
                <th>Person</th>
                <th>Role</th>
                <th>Last sign-in</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(members.data ?? []).map((m) => (
                <tr key={m.id}>
                  <td>
                    <b>{m.name ?? m.email}</b>
                    <div className="muted xs">{m.email}</div>
                  </td>
                  <td>{roleLabel(m.role)}</td>
                  <td>{m.last_login_at ? fmtDate(m.last_login_at) : "Invited"}</td>
                  <td>
                    {m.id !== me?.user.id && (
                      <button className="btn ghost small" onClick={async () => {
                        await api.del(`/api/t/${tenant}/members/${m.id}`);
                        await members.reload();
                      }}>
                        Remove access
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        <form className="panel row wrap" onSubmit={add} style={{ alignItems: "flex-end" }}>
          <label className="field" style={{ flex: "1 1 260px" }}>
            <span>Work email</span>
            <input className="input" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required />
          </label>
          <label className="field" style={{ flex: "0 1 200px" }}>
            <span>Role</span>
            <select className="input" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {roleLabel(r)}
                </option>
              ))}
            </select>
          </label>
          <button className="btn primary">Give access</button>
          <ErrorNote error={error} />
        </form>
        <div className="grid2">
          <section className="panel">
            <div className="panel-head">
              <h3>Usage</h3>
              <p>{usage.data?.plan} plan{usage.data?.remainingSeconds != null ? ` · ${fmtDuration(usage.data.remainingSeconds)} interview time left` : ""}</p>
            </div>
            <table className="table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Interview time</th>
                  <th>AI tokens</th>
                </tr>
              </thead>
              <tbody>
                {(usage.data?.months ?? []).map((u) => (
                  <tr key={u.period}>
                    <td>{u.period}</td>
                    <td>{fmtDuration(u.interview_seconds)}</td>
                    <td>{(Number(u.ai_input_tokens) + Number(u.ai_output_tokens)).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
          <section className="panel">
            <div className="panel-head">
              <h3>Recent activity</h3>
              <p>From the tamper-evident audit log</p>
            </div>
            <div style={{ maxHeight: 340, overflow: "auto" }}>
              {(audit.data ?? []).map((a) => (
                <div key={a.id} className="row small" style={{ padding: "5px 0", borderBottom: "1px solid var(--line)" }}>
                  <span className="faint" style={{ minWidth: 120 }}>{new Date(a.at).toLocaleString()}</span>
                  <span>{a.action.replace(/[._]/g, " ")}</span>
                  <span className="spacer" />
                  <span className="muted xs">{a.actor_email ?? "system"}</span>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </>
  );
}
